import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import streamApi from '../../services/streamApi';
import { authApi, clearStoredAuth } from '../../services/authApi';
import { ROUTES } from '../../constants';
import { useNavbarUser } from '../../utils/navbarUser';
import './UploadPage.css';

function UploadPage() {
  const navigate = useNavigate();
  const user = useNavbarUser();
  const streamConfigError = streamApi.getConfigurationError();
  const isStreamConfigured = !streamConfigError;

  const [isConnected, setIsConnected] = useState(!!streamApi.getStreamToken());
  const [connecting, setConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('media');
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [submitMediaId, setSubmitMediaId] = useState('');
  const [submitStreamerUsername, setSubmitStreamerUsername] = useState('');
  const [submitDuration, setSubmitDuration] = useState(10);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitLookupLoading, setSubmitLookupLoading] = useState(false);
  const [submitLookupError, setSubmitLookupError] = useState('');
  const [submitResolvedStreamer, setSubmitResolvedStreamer] = useState(null);
  const [submitSearchResults, setSubmitSearchResults] = useState([]);
  const [submitStreamerMenuOpen, setSubmitStreamerMenuOpen] = useState(false);
  const [submitSearchHighlightIndex, setSubmitSearchHighlightIndex] = useState(-1);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fanSubmissions, setFanSubmissions] = useState([]);
  const [fanSubLoading, setFanSubLoading] = useState(false);
  const submitComboboxRef = useRef(null);
  const submitSearchInputRef = useRef(null);
  const isUploadDisabled = !isConnected || uploadLoading;
  const approvedMedia = mediaLibrary.filter((item) => item.approval_status === 'approved');
  const trimmedSubmitStreamerUsername = submitStreamerUsername.trim();
  const selectedStreamerMatchesQuery = (
    !!submitResolvedStreamer?.username
    && submitResolvedStreamer.username.toLowerCase() === trimmedSubmitStreamerUsername.toLowerCase()
  );
  const isSubmitDisabled = (
    !isConnected
    || submitLoading
    || !submitMediaId
    || !trimmedSubmitStreamerUsername
    || !selectedStreamerMatchesQuery
    || !approvedMedia.length
  );
  const selectedMedia = approvedMedia.find((item) => item.id === submitMediaId);
  const estimatedCost = selectedMedia
    ? submitDuration * ((selectedMedia.media_type || 'image') === 'gif' ? 300 : 200)
    : null;

  const loadMediaLibrary = useCallback(async () => {
    if (!isStreamConfigured) {
      setMediaLibrary([]);
      return;
    }

    setMediaLoading(true);
    try {
      const data = await streamApi.getMediaLibrary();
      setMediaLibrary(Array.isArray(data) ? data : []);
    } catch (error) {
      setMediaLibrary([]);
      setUploadError(error.message || 'Failed to load your media library');
    } finally {
      setMediaLoading(false);
    }
  }, [isStreamConfigured]);

  const loadFanSubmissions = useCallback(async () => {
    if (!isStreamConfigured) {
      setFanSubmissions([]);
      return;
    }

    setFanSubLoading(true);
    try {
      const data = await streamApi.getFanSubmissions();
      setFanSubmissions(Array.isArray(data) ? data : []);
    } catch {
      setFanSubmissions([]);
    } finally {
      setFanSubLoading(false);
    }
  }, [isStreamConfigured]);

  useEffect(() => {
    if (!isStreamConfigured || isConnected) {
      setConnecting(false);
      return;
    }

    let cancelled = false;

    setConnecting(true);
    streamApi.autoConnect('fan').then((ok) => {
      if (cancelled) {
        return;
      }
      setIsConnected(ok);
      setConnecting(false);
      if (!ok) {
        setUploadError(
          streamApi.getLastConnectionError() || 'Could not connect to the upload backend for this account.'
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isConnected, isStreamConfigured]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    loadMediaLibrary();
  }, [isConnected, loadMediaLibrary]);

  useEffect(() => {
    if (!isConnected || activeTab !== 'submit') {
      return;
    }

    loadFanSubmissions();
  }, [activeTab, isConnected, loadFanSubmissions]);

  useEffect(() => {
    if (!isConnected || activeTab !== 'submit') {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadFanSubmissions();
    }, 15000);

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        loadFanSubmissions();
      }
    };

    document.addEventListener('visibilitychange', handleVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [activeTab, isConnected, loadFanSubmissions]);

  useEffect(() => {
    if (activeTab !== 'submit') {
      return undefined;
    }

    if (!submitStreamerMenuOpen) {
      return undefined;
    }

    if (trimmedSubmitStreamerUsername.length < 2) {
      setSubmitLookupLoading(false);
      setSubmitLookupError('');
      setSubmitSearchResults([]);
      setSubmitSearchHighlightIndex(-1);
      return undefined;
    }

    if (selectedStreamerMatchesQuery) {
      setSubmitLookupLoading(false);
      setSubmitLookupError('');
      setSubmitSearchResults([]);
      setSubmitSearchHighlightIndex(-1);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setSubmitLookupLoading(true);
      try {
        const results = await authApi.searchUsers(trimmedSubmitStreamerUsername);
        if (cancelled) {
          return;
        }

        const exactMatch = results.find(
          (item) => item.username?.toLowerCase() === trimmedSubmitStreamerUsername.toLowerCase()
        );

        setSubmitSearchResults(results);
        setSubmitResolvedStreamer(exactMatch || null);
        setSubmitSearchHighlightIndex(results.length > 0 ? 0 : -1);
        setSubmitLookupError(results.length === 0 ? 'No matching streamer usernames found.' : '');
      } catch (error) {
        if (cancelled) {
          return;
        }

        setSubmitSearchResults([]);
        setSubmitResolvedStreamer(null);
        setSubmitSearchHighlightIndex(-1);
        setSubmitLookupError(error.message || 'Failed to search streamer usernames');
      } finally {
        if (!cancelled) {
          setSubmitLookupLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [activeTab, selectedStreamerMatchesQuery, submitStreamerMenuOpen, trimmedSubmitStreamerUsername]);

  useEffect(() => {
    if (!submitStreamerMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (submitComboboxRef.current && !submitComboboxRef.current.contains(event.target)) {
        setSubmitStreamerMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setSubmitStreamerMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [submitStreamerMenuOpen]);

  useEffect(() => {
    if (!submitStreamerMenuOpen) {
      return;
    }

    window.setTimeout(() => {
      submitSearchInputRef.current?.focus();
      submitSearchInputRef.current?.select();
    }, 0);
  }, [submitStreamerMenuOpen]);

  async function handleLogout() {
    await authApi.logout();
    clearStoredAuth();
    streamApi.clearStreamAuth();
    navigate(ROUTES.WELCOME);
  }

  async function handleUploadMedia(event) {
    event.preventDefault();

    if (!uploadTitle.trim() || !uploadFile || uploadLoading || !isConnected) {
      return;
    }

    setUploadLoading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      await streamApi.uploadMedia(uploadTitle, uploadFile);
      setUploadTitle('');
      setUploadFile(null);
      setUploadSuccess('Media uploaded. It is now waiting for admin review.');
      await loadMediaLibrary();
    } catch (error) {
      setUploadError(error.message || 'Failed to upload media');
    } finally {
      setUploadLoading(false);
    }
  }

  async function handleDeleteMedia(mediaId) {
    try {
      setUploadError('');
      await streamApi.deleteMedia(mediaId);
      await loadMediaLibrary();
    } catch (error) {
      setUploadError(error.message || 'Failed to delete media');
    }
  }

  async function handleCreateSubmission(event) {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    setSubmitLoading(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const streamer = selectedStreamerMatchesQuery
        ? submitResolvedStreamer
        : await authApi.getUserByUsername(trimmedSubmitStreamerUsername);
      setSubmitResolvedStreamer(streamer);

      const result = await streamApi.createSubmission({
        media_id: submitMediaId,
        streamer_id: streamer.id,
        duration_minutes: submitDuration,
      });

      setSubmitSuccess(
        `Submission created for ${streamer.username || trimmedSubmitStreamerUsername}. ${result.spades_cost || estimatedCost || 0} spades reserved for this request.`
      );
      setSubmitMediaId('');
      setSubmitStreamerUsername('');
      setSubmitDuration(10);
      setSubmitLookupError('');
      setSubmitResolvedStreamer(null);
      setSubmitSearchResults([]);

      await Promise.all([
        loadFanSubmissions(),
        authApi.getPaymentBalance().catch(() => null),
      ]);
    } catch (error) {
      setSubmitError(error.message || 'Failed to create submission');
    } finally {
      setSubmitLoading(false);
    }
  }

  function handleSelectStreamer(streamer) {
    setSubmitStreamerUsername(streamer.username || '');
    setSubmitResolvedStreamer(streamer);
    setSubmitLookupError('');
    setSubmitSearchResults([]);
    setSubmitSearchHighlightIndex(-1);
    setSubmitError('');
    setSubmitStreamerMenuOpen(false);
  }

  function handleStreamerUsernameChange(event) {
    const nextValue = event.target.value;

    setSubmitStreamerUsername(nextValue);
    setSubmitError('');
    setSubmitSuccess('');
    setSubmitLookupError('');

    if (
      !submitResolvedStreamer?.username
      || submitResolvedStreamer.username.toLowerCase() !== nextValue.trim().toLowerCase()
    ) {
      setSubmitResolvedStreamer(null);
    }
  }

  function handleStreamerComboboxToggle() {
    setSubmitStreamerMenuOpen((prev) => !prev);
    setSubmitError('');
    setSubmitSuccess('');
  }

  function handleStreamerSearchKeyDown(event) {
    if (event.key === 'Escape') {
      setSubmitStreamerMenuOpen(false);
      return;
    }

    if (!submitSearchResults.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSubmitSearchHighlightIndex((prev) => (
        prev < submitSearchResults.length - 1 ? prev + 1 : 0
      ));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSubmitSearchHighlightIndex((prev) => (
        prev > 0 ? prev - 1 : submitSearchResults.length - 1
      ));
      return;
    }

    if (event.key === 'Enter' && submitSearchHighlightIndex >= 0) {
      event.preventDefault();
      handleSelectStreamer(submitSearchResults[submitSearchHighlightIndex]);
    }
  }

  function handleClearStreamerSelection() {
    setSubmitStreamerUsername('');
    setSubmitResolvedStreamer(null);
    setSubmitSearchResults([]);
    setSubmitSearchHighlightIndex(-1);
    setSubmitLookupError('');
    setSubmitError('');
    setSubmitSuccess('');
    setSubmitStreamerMenuOpen(true);
  }

  return (
    <div className="upload-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        minimal
      />

      <LeftSidebar />

      <main className="upload-page__main">
        <div className="upload-page__content">
          <h1 className="upload-page__title">Upload</h1>
          <p className="upload-page__subtitle">
            Upload media and submit it to streamers for display on their streams.
          </p>

          {connecting && (
            <div className="upload-page__connecting">Connecting to upload backend...</div>
          )}

          {streamConfigError && (
            <div className="upload-page__warning">{streamConfigError}</div>
          )}

          <div className="upload-page__tabs">
            <button
              type="button"
              className={`upload-page__tab ${activeTab === 'media' ? 'upload-page__tab--active' : ''}`}
              onClick={() => setActiveTab('media')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              My Media
            </button>
            <button
              type="button"
              className={`upload-page__tab ${activeTab === 'submit' ? 'upload-page__tab--active' : ''}`}
              onClick={() => setActiveTab('submit')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Submit to Streamer
            </button>
          </div>

          {activeTab === 'media' && (
            <div className="upload-page__section">
              <section className="upload-page__card upload-page__upload-card">
                <h2 className="upload-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload Media
                </h2>

                <form className="upload-page__form" onSubmit={handleUploadMedia}>
                  <div className="upload-page__field">
                    <label className="upload-page__label">Title</label>
                    <input
                      type="text"
                      className="upload-page__input"
                      value={uploadTitle}
                      onChange={(event) => setUploadTitle(event.target.value)}
                      placeholder="My awesome image"
                      disabled={isUploadDisabled}
                      required
                    />
                  </div>

                  <div className="upload-page__field">
                    <label className="upload-page__label">Image File</label>
                    <div className="upload-page__file-wrapper">
                      <input
                        id="upload-media-file-input"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif"
                        className="upload-page__file-input"
                        onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                        disabled={isUploadDisabled}
                      />
                      <label
                        htmlFor="upload-media-file-input"
                        className={`upload-page__file-display ${isUploadDisabled ? 'upload-page__file-display--disabled' : ''}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                        <span className="upload-page__file-name">
                          {uploadFile ? uploadFile.name : 'Choose an image or GIF...'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="upload-page__btn"
                    disabled={isUploadDisabled || !uploadTitle.trim() || !uploadFile}
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                  </button>
                </form>

                {uploadError && <div className="upload-page__msg upload-page__msg--error">{uploadError}</div>}
                {uploadSuccess && <div className="upload-page__msg upload-page__msg--success">{uploadSuccess}</div>}
              </section>

              <section className="upload-page__card">
                <h2 className="upload-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  My Media Library
                  {mediaLibrary.length > 0 && (
                    <span className="upload-page__badge-count">{mediaLibrary.length}</span>
                  )}
                </h2>

                {mediaLoading ? (
                  <div className="upload-page__empty">Loading media...</div>
                ) : mediaLibrary.length === 0 ? (
                  <div className="upload-page__empty">
                    No media uploaded yet. Upload your first image above!
                  </div>
                ) : (
                  <div className="upload-page__media-grid">
                    {mediaLibrary.map((item) => {
                      const previewUrl = item.image_preview_url || streamApi.resolveImageUrl(item.image_url);
                      return (
                        <article key={item.id} className="upload-page__media-item">
                          <div className="upload-page__media-thumb">
                            {previewUrl ? (
                              <img src={previewUrl} alt={item.title} />
                            ) : (
                              <div className="upload-page__media-no-image">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21 15 16 10 5 21" />
                                </svg>
                              </div>
                            )}
                            <span className={`upload-page__media-status upload-page__media-status--${item.approval_status || 'pending'}`}>
                              {item.approval_status || 'pending'}
                            </span>
                          </div>
                          <div className="upload-page__media-info">
                            <span className="upload-page__media-title">{item.title}</span>
                            <span className="upload-page__media-type">{item.media_type || 'image'}</span>
                          </div>
                          <button
                            type="button"
                            className="upload-page__media-delete"
                            onClick={() => handleDeleteMedia(item.id)}
                            title="Delete media"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === 'submit' && (
            <div className="upload-page__section">
              <section className="upload-page__card upload-page__submit-card">
                <h2 className="upload-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Submit to Streamer
                </h2>
                <p className="upload-page__submit-desc">
                  Select approved media and send it to a streamer by username.
                </p>

                <form className="upload-page__form" onSubmit={handleCreateSubmission}>
                  <div className="upload-page__field">
                    <label className="upload-page__label">Select Media</label>
                    <select
                      className="upload-page__select"
                      value={submitMediaId}
                      onChange={(event) => setSubmitMediaId(event.target.value)}
                    >
                      <option value="">-- Choose approved media --</option>
                      {approvedMedia.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title} ({item.media_type || 'image'})
                        </option>
                      ))}
                    </select>
                    {approvedMedia.length === 0 && (
                      <span className="upload-page__hint">
                        No approved media yet. Upload media first and wait for admin approval.
                      </span>
                    )}
                  </div>

                  <div className="upload-page__field">
                    <label className="upload-page__label">Streamer Username</label>
                    <div className="upload-page__search" ref={submitComboboxRef}>
                      <button
                        type="button"
                        className={`upload-page__combobox-trigger ${submitStreamerMenuOpen ? 'upload-page__combobox-trigger--open' : ''}`}
                        onClick={handleStreamerComboboxToggle}
                        aria-haspopup="listbox"
                        aria-expanded={submitStreamerMenuOpen}
                      >
                        <span className={`upload-page__combobox-value ${selectedStreamerMatchesQuery ? 'upload-page__combobox-value--selected' : ''}`}>
                          {trimmedSubmitStreamerUsername || 'Select a Polar Ace user'}
                        </span>
                        <span className="upload-page__combobox-caret" aria-hidden="true">
                          {submitStreamerMenuOpen ? '▲' : '▼'}
                        </span>
                      </button>

                      {submitStreamerMenuOpen && (
                        <div className="upload-page__combobox-panel">
                          <div className="upload-page__combobox-search">
                            <input
                              ref={submitSearchInputRef}
                              type="text"
                              className="upload-page__input upload-page__combobox-input"
                              value={submitStreamerUsername}
                              onChange={handleStreamerUsernameChange}
                              onKeyDown={handleStreamerSearchKeyDown}
                              placeholder="Search by Polar Ace username"
                              autoComplete="off"
                            />
                            {trimmedSubmitStreamerUsername && (
                              <button
                                type="button"
                                className="upload-page__combobox-clear"
                                onClick={handleClearStreamerSelection}
                                aria-label="Clear streamer selection"
                              >
                                ×
                              </button>
                            )}
                          </div>

                          {trimmedSubmitStreamerUsername.length < 2 ? (
                            <div className="upload-page__search-results upload-page__search-results--status">
                              Type at least 2 characters to search Polar Ace users.
                            </div>
                          ) : submitLookupLoading ? (
                            <div className="upload-page__search-results upload-page__search-results--status">
                              Searching streamer usernames...
                            </div>
                          ) : submitSearchResults.length > 0 ? (
                            <div className="upload-page__search-results" role="listbox" aria-label="Streamer search results">
                              {submitSearchResults.map((streamer, index) => (
                                <button
                                  key={streamer.id}
                                  type="button"
                                  className={`upload-page__search-result ${index === submitSearchHighlightIndex ? 'upload-page__search-result--active' : ''}`}
                                  onClick={() => handleSelectStreamer(streamer)}
                                >
                                  <span className="upload-page__search-username">{streamer.username}</span>
                                </button>
                              ))}
                            </div>
                          ) : submitLookupError ? (
                            <div className="upload-page__search-results upload-page__search-results--status">
                              {submitLookupError}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {selectedStreamerMatchesQuery && (
                      <span className="upload-page__hint">
                        Sending to {submitResolvedStreamer.username}
                      </span>
                    )}
                  </div>

                  <div className="upload-page__field">
                    <label className="upload-page__label">Duration (minutes)</label>
                    <input
                      type="number"
                      className="upload-page__input"
                      value={submitDuration}
                      onChange={(event) => setSubmitDuration(Math.max(1, parseInt(event.target.value, 10) || 1))}
                      min="1"
                      max="60"
                    />
                  </div>

                  {estimatedCost !== null && (
                    <div className="upload-page__cost-preview">
                      <span className="upload-page__cost-label">Estimated Cost</span>
                      <span className="upload-page__cost-value">
                        <span className="upload-page__cost-icon">&#9824;</span>
                        {estimatedCost.toLocaleString()} spades
                      </span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="upload-page__btn"
                    disabled={isSubmitDisabled}
                  >
                    {submitLoading ? 'Submitting...' : 'Submit to Streamer'}
                  </button>

                  {submitError && <div className="upload-page__msg upload-page__msg--error">{submitError}</div>}
                  {submitSuccess && <div className="upload-page__msg upload-page__msg--success">{submitSuccess}</div>}
                </form>
              </section>

              <section className="upload-page__card">
                <h2 className="upload-page__card-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  My Submission History
                  {fanSubmissions.length > 0 && (
                    <span className="upload-page__badge-count">{fanSubmissions.length}</span>
                  )}
                </h2>

                {fanSubLoading ? (
                  <div className="upload-page__empty">Loading submissions...</div>
                ) : fanSubmissions.length === 0 ? (
                  <div className="upload-page__empty">
                    No submissions yet.
                  </div>
                ) : (
                  <table className="upload-page__table">
                    <thead>
                      <tr>
                        <th>Streamer</th>
                        <th>Media</th>
                        <th>Duration</th>
                        <th>Cost</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fanSubmissions.map((submission) => (
                        <tr key={submission.id}>
                          <td>{submission.streamer_username || submission.streamer_id || '-'}</td>
                          <td>{submission.media_title || submission.media_id || '-'}</td>
                          <td>{submission.duration_minutes || 10} min</td>
                          <td>{submission.spades_cost || '-'}</td>
                          <td>
                            <span className={`upload-page__status upload-page__status--${submission.status || 'pending'}`}>
                              {submission.payment_status === 'refunded'
                                ? 'refunded'
                                : submission.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </div>
          )}
        </div>
        <Footer />
      </main>
    </div>
  );
}

export default UploadPage;
